import '../../../scss/hotfood/components/production-view.scss'

import {
  Body1,
  Body2,
  Box,
  Button,
  Container,
  Display1,
  Display2,
  Flex,
  Grid,
  GridItem,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled } from '@sainsburys-tech/icons'
import dayjs from 'dayjs'
import moment from 'moment'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import BannerHolder from '../../../common/components/Banners/BannerHolder'
import ConfirmationBanner from '../../../common/components/Banners/ConfirmationBanner'
import ErrorBanner from '../../../common/components/Banners/ErrorBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import {
  addBannerMessage,
  setBannerMessages,
} from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { checkStore, getUserStore } from '../../../utils/localStore'
import { getCounters } from '../../api/counterApi'
import {
  createCounterAvailability,
  getBatches,
  getCounterAvailability,
} from '../../api/productionApi'
import { IBatch } from '../../types/IBatch'
import { ICounterAvailability } from '../../types/ICounterAvailability'
import { IStackBanner } from '../../types/IStackBanner'
import { ITemperatureData } from '../../types/ITemperatureData'
import { afternoonHour, getTimeNow } from '../../utils/hotFoodUtils'
import AddNewBatchCard from './AddNewBatchCard'
import BatchCard from './BatchCard'
import CloseCounterModal from './CloseCounterModal'

const ProductionView = () => {
  const { dispatch, state } = useContext(Context)
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [batches, setBatches] = useState<IBatch[]>([])
  const [isCloseCounterModalOpen, setIsCloseCounterModalOpen] = useState(false)
  const [isCounterClosed, setIsCounterClosed] = useState(false)
  const [isTempCheckDue, setIsTempCheckDue] = useState(false)
  const [tempNotTaken, setTempNotTaken] = useState(false)
  const [lunchOvenStartTime, setLunchOvenStartTime] = useState<
    Date | undefined
  >(undefined)
  const varMissingPmTempChecksRef = useRef(false)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const onCounterAvailabilityChange = useCallback(
    async (isActive: boolean, reasonId: number, reasonDescription: string) => {
      setIsCloseCounterModalOpen(false)

      const counterAvailability: ICounterAvailability = {
        isActive,
        reasonDescription,
        reasonId,
        storeId: getUserStore().storeId,
      }

      try {
        const res = await createCounterAvailability(
          apiClient,
          counterAvailability,
        )
        if (res.isSuccess) {
          dispatch(
            addBannerMessage(
              <ConfirmationBanner
                message={`Hot food counter has been ${isActive ? 're-opened' : 'closed'}.`}
              />,
            ),
          )
          setIsCounterClosed(!isActive)
        } else {
          handleError(ErrorMessage.SaveException)
        }
      } catch (e) {
        handleError(ErrorMessage.SaveException, e as Error)
      }
    },
    [
      apiClient,
      dispatch,
      setIsCloseCounterModalOpen,
      setIsCounterClosed,
      handleError,
    ],
  )

  const bannerCheck = useCallback(() => {
    if (
      varMissingPmTempChecksRef.current &&
      getTimeNow().getHours() >= afternoonHour
    ) {
      setIsTempCheckDue(true)
    }
  }, [])

  const refresh = useCallback(() => {
    bannerCheck()
  }, [bannerCheck])

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.HotFood,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  // separated useEffect to manage interval handle as it helps to:
  // - recreate the new function every re-render, ensuring the latest logic is used
  // - prevent memory leaks by cleaning up the interval properly
  // - avoid referencing an outdated interval function
  useEffect(() => {
    const intervalId = setInterval(refresh, 10000)

    return () => {
      clearInterval(intervalId)
    }
  }, [refresh])

  useEffect(() => {
    const loadHotFoodData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [
          counterAvailabilityData,
          batchesData,
          countersData,
          isStoreZeroErrorsEnabled,
        ] = await Promise.all([
          getCounterAvailability(apiClient, store.storeId, new Date()),
          getBatches(apiClient, store.storeId, new Date()),
          getCounters(
            apiClient,
            store.storeId,
            moment(new Date(Date.now())).toDate(),
          ),
          isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
        ])

        setIsCounterClosed(!counterAvailabilityData.isActive)

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }

        const [planBatches, userBatches] = batchesData.reduce(
          (acc: IBatch[][], i: IBatch) => {
            acc[!i.isUserCreated ? 0 : 1].push(i)
            return acc
          },
          [[], []],
        )

        // Convenience Stores (local) : add index of the autogenerated batches by cronjob to use at the title index
        const sortedPlanBatches = planBatches
          .filter((b) => b.programId !== null)
          .sort((a, b) =>
            a.productionTime === undefined
              ? 1
              : b.productionTime === undefined
                ? -1
                : a.productionTime > b.productionTime
                  ? 1
                  : -1,
          )

        // set lunch oven start time based on the production time of the first lunch batch
        // exeptional case (unrealistic case) : a breakfast batch remains, but all done for lunch batches
        // -> set 'lunch oven start time' to 'last breakfast batch time + min cooktime(10min) of programs'
        const firstLunchBatchTime =
          !sortedPlanBatches || sortedPlanBatches.length === 0
            ? undefined
            : sortedPlanBatches.filter((b) => !b.isBreakfastLine).length > 0
              ? (sortedPlanBatches.filter((b) => !b.isBreakfastLine)[0]
                  .productionTime as Date)
              : dayjs(
                  sortedPlanBatches.filter((b) => b.isBreakfastLine).pop()
                    ?.productionTime,
                )
                  .add(10, 'minutes')
                  .toDate()

        setLunchOvenStartTime(firstLunchBatchTime)

        // split userBatches by the production time of the first lunch batch
        const splitUserBatches = userBatches.reduce(
          (acc: IBatch[], cur: IBatch) => [
            ...acc,
            {
              ...cur,
              isBreakfastLine:
                !firstLunchBatchTime || !cur.productionTime
                  ? undefined
                  : dayjs(firstLunchBatchTime).diff(
                      dayjs(userBatches[0].productionTime),
                    ) > 0,
            },
          ],
          [],
        )

        setBatches([...splitUserBatches, ...sortedPlanBatches])

        const nonFaultyCabinets = countersData
          .filter((counter) => !counter.isFaulty)
          .map((counter) => {
            return {
              counterNumber: counter.counterNumber,
              isFaulty: counter.isFaulty,
              takenAt: counter.takenAt,
              temperatures: undefined,
            }
          })

        // reset the ref at the start of loading
        varMissingPmTempChecksRef.current = false

        // get unique counter numbers
        const counterNumbers = [
          ...new Set(nonFaultyCabinets.map((counter) => counter.counterNumber)),
        ]

        counterNumbers.forEach((num) => {
          // if no PM check exists for any of the unique counter(s), flag as missing
          varMissingPmTempChecksRef.current =
            varMissingPmTempChecksRef.current ||
            nonFaultyCabinets.filter(
              (counter) =>
                counter.counterNumber === num &&
                counter.takenAt !== null &&
                moment.utc(counter.takenAt).local().get('hours') >=
                  afternoonHour,
            ).length === 0
        })

        const okCounters = countersData.filter((counter) => !counter.isFaulty)
        setTempNotTaken(
          okCounters.filter((counter) => counter.takenAt === null).length > 0,
        )

        if (varMissingPmTempChecksRef.current) {
          bannerCheck()
        }
      } catch (e) {
        handleError(ErrorMessage.ProductionView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadHotFoodData()
  }, [apiClient, store, handleError, resetError, bannerCheck])

  const removeBatch = (batchId: number) =>
    setBatches(batches.filter((b: IBatch) => b.batchId !== batchId))

  const updateTemperatures = (
    batchId: number,
    temperatures: ITemperatureData[],
  ) => {
    batches.filter((b: IBatch) => b.batchId === batchId)[0].temperatures =
      temperatures.filter((t) => t.temperature !== '')
    setBatches(batches)
  }

  const updateTemperaturesCount = (
    batchId: number,
    temperaturesCount: number,
  ) => {
    batches.filter((b: IBatch) => b.batchId === batchId)[0].temperaturesCount =
      temperaturesCount
    setBatches(batches)
  }

  const counterTemperatureLink = (): JSX.Element => (
    <Link
      to={{
        pathname: '/hotfoodcountertemperature',
        search: '',
      }}
      style={{
        color: 'var(--fable-color-icon-static-light, #ffffff)',
      }}
    >
      Counter temperature
    </Link>
  )

  const saveBanners = (banners: IStackBanner[]) => {
    dispatch(setBannerMessages(banners))
  }

  const isBreakfastOrLunchBatch = () =>
    // can show the 'add' button and user-batches even if
    // - there is no batche at all or
    // - no plan-batche or
    // - any undefined in isBreakfastLine flag of batches
    lunchOvenStartTime &&
    batches.length > 0 &&
    batches.filter((b: IBatch) => !b.isUserCreated).length > 0 &&
    batches.filter(
      (b: IBatch) => b.isBreakfastLine === true || b.isBreakfastLine === false,
    ).length > 0

  const buildBannerMessageForNotTaken = () => {
    const headerMessage = tempNotTaken
      ? ' your AM Flexeserve temperature check '
      : ''
    return (
      <>
        <Body1>
          Please complete {headerMessage}
          in the '{counterTemperatureLink()}' tab before adding any products to
          the display.
        </Body1>
      </>
    )
  }

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && (
        <Box padding='none' backgroundColor='primary'>
          <Header
            title={headerPlannerTitles.hotfood}
            navItems={headerNavigationItems}
            activeMenuType={Menu.ProductionView}
          />

          <Container className='c-common-main-view-content'>
            {!isLoading && errorMessage && (
              <ErrorRefresh message={errorMessage} />
            )}

            {!isLoading && !errorMessage && (
              <>
                {tempNotTaken && !isCounterClosed && (
                  <ErrorBanner
                    icon={<AlertCircleFilled />}
                    message={''}
                    messageObject={buildBannerMessageForNotTaken()}
                  />
                )}

                {isTempCheckDue && !isCounterClosed && (
                  <ErrorBanner
                    icon={<AlertCircleFilled />}
                    message={''}
                    messageObject={
                      <>
                        Please complete your PM Flexeserve temperature reading
                        in the '{counterTemperatureLink()}' tab by the end of
                        the day.
                      </>
                    }
                  />
                )}

                <BannerHolder
                  banners={state.bannerMessages}
                  setBanners={saveBanners}
                />
                <div></div>

                <Box padding={'none'} className='hotfood-production-header'>
                  <div className='hotfood-production-heading-group'>
                    <Display1 className='hotfood-production-title' as={'h2'}>
                      Today's production
                    </Display1>
                    <Body1 className='hotfood-production-date'>
                      {`${moment(new Date(Date.now())).format('dddd Do MMMM YYYY')}.`}
                    </Body1>
                  </div>
                  {!isCounterClosed && (
                    <div className='hotfood-production-header-action'>
                      <Button
                        className='c-availability-button'
                        onClick={() => setIsCloseCounterModalOpen(true)}
                        variant='secondary'
                        testid='close-counter-button'
                      >
                        Set counter to closed
                      </Button>
                    </div>
                  )}
                </Box>

                {!isCounterClosed && (
                  <>
                    {!isBreakfastOrLunchBatch() ? (
                      <>
                        <Grid
                          cols={12}
                          gap='lg'
                          as={'section'}
                          className='hotfood-batch-grid'
                        >
                          {batches?.map((batch: IBatch, i: number) => (
                            <GridItem
                              key={i}
                              colSpan={{
                                default: 12,
                                sm: 6,
                                md: 6,
                                lg: 4,
                              }}
                              className='hotfood-batch-grid-item'
                            >
                              <Box className='hotfood-batch-card-wrapper'>
                                <BatchCard
                                  batch={batch}
                                  batchIndex={batch.planBatchIndex}
                                  counterTemperaturesNotTaken={tempNotTaken}
                                  removeBatch={removeBatch}
                                  updateTemperatures={updateTemperatures}
                                  updateTemperaturesCount={
                                    updateTemperaturesCount
                                  }
                                  setErrorMessage={setErrorMessage}
                                />
                              </Box>
                            </GridItem>
                          ))}
                          <GridItem
                            colSpan={{
                              default: 12,
                              sm: 6,
                              md: 6,
                              lg: 4,
                            }}
                            className='hotfood-batch-grid-item'
                          >
                            <Box className='hotfood-batch-card-wrapper'>
                              <AddNewBatchCard />
                            </Box>
                          </GridItem>
                        </Grid>
                      </>
                    ) : (
                      <>
                        {batches.filter(
                          (b: IBatch) => b.isBreakfastLine === true,
                        ).length > 0 && (
                          <>
                            <Display2 as={'h3'}>Breakfast</Display2>
                            <Grid
                              cols={12}
                              gap='lg'
                              className='hotfood-batch-grid'
                            >
                              {batches
                                .filter((b: IBatch) => b.isBreakfastLine)
                                .map((batch: IBatch, i: number) => (
                                  <GridItem
                                    key={i}
                                    colSpan={{
                                      default: 12,
                                      sm: 6,
                                      md: 6,
                                      lg: 4,
                                    }}
                                    className='hotfood-batch-grid-item'
                                  >
                                    <Box className='hotfood-batch-card-wrapper'>
                                      <BatchCard
                                        batch={batch}
                                        batchIndex={batch.planBatchIndex}
                                        counterTemperaturesNotTaken={
                                          tempNotTaken
                                        }
                                        removeBatch={removeBatch}
                                        updateTemperatures={updateTemperatures}
                                        updateTemperaturesCount={
                                          updateTemperaturesCount
                                        }
                                        setErrorMessage={setErrorMessage}
                                      />
                                    </Box>
                                  </GridItem>
                                ))}
                              <GridItem
                                colSpan={{
                                  default: 12,
                                  sm: 6,
                                  md: 6,
                                  lg: 4,
                                }}
                                className='hotfood-batch-grid-item'
                              >
                                <Box className='hotfood-batch-card-wrapper'>
                                  <AddNewBatchCard />
                                </Box>
                              </GridItem>
                            </Grid>
                          </>
                        )}

                        {batches.filter(
                          (b: IBatch) => b.isBreakfastLine === false,
                        ).length > 0 && (
                          <>
                            <Display2 as={'h3'}>Lunch</Display2>
                            <Grid
                              cols={12}
                              gap='lg'
                              className='hotfood-batch-grid'
                            >
                              {batches
                                .filter((b) => !b.isBreakfastLine)
                                .map((batch: IBatch, i: number) => (
                                  <GridItem
                                    key={i}
                                    colSpan={{
                                      default: 12,
                                      sm: 6,
                                      md: 6,
                                      lg: 4,
                                    }}
                                    className='hotfood-batch-grid-item'
                                  >
                                    <Box className='hotfood-batch-card-wrapper'>
                                      <BatchCard
                                        batch={batch}
                                        batchIndex={batch.planBatchIndex}
                                        counterTemperaturesNotTaken={
                                          tempNotTaken
                                        }
                                        removeBatch={removeBatch}
                                        updateTemperatures={updateTemperatures}
                                        updateTemperaturesCount={
                                          updateTemperaturesCount
                                        }
                                        setErrorMessage={setErrorMessage}
                                      />
                                    </Box>
                                  </GridItem>
                                ))}
                              {!batches.some(
                                (b: IBatch) => b.isBreakfastLine,
                              ) && (
                                <GridItem
                                  colSpan={{
                                    default: 12,
                                    sm: 6,
                                    md: 6,
                                    lg: 4,
                                  }}
                                  className='hotfood-batch-grid-item'
                                >
                                  <Box className='hotfood-batch-card-wrapper'>
                                    <AddNewBatchCard />
                                  </Box>
                                </GridItem>
                              )}
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {isCounterClosed && (
                  <div className='c-no-availability'>
                    <Flex alignItems='center' direction='column' gap='sm'>
                      <Flex direction='row' alignItems='center' gap='md'>
                        <AlertCircleFilled className='c-no-availability-icon' />
                        <Body2
                          className='c-no-availability-header-text'
                          style={{ fontWeight: 'bold', fontSize: '1.5rem' }}
                        >
                          Your counter has been marked as closed
                        </Body2>
                      </Flex>
                      <Flex
                        direction='column'
                        alignItems='flex-start'
                        className='c-no-availability-text-button-group'
                      >
                        <div className='c-no-availability-text'>
                          <Body1>
                            There are no batches available as this counter has
                            been marked as closed. You can re-open the counter
                            in the button below once it is fixed or available
                            for use again.
                          </Body1>
                        </div>
                        <Button
                          className='c-no-availability-button'
                          onClick={() =>
                            onCounterAvailabilityChange(true, 0, '')
                          }
                          testid='re-open-button'
                          isFullWidth={false}
                        >
                          Re-open counter
                        </Button>
                      </Flex>
                    </Flex>
                  </div>
                )}
              </>
            )}

            {isCloseCounterModalOpen && (
              <CloseCounterModal
                handleClose={() => setIsCloseCounterModalOpen(false)}
                handleSave={(reasonId: number, reasonDescription: string) =>
                  onCounterAvailabilityChange(
                    false,
                    reasonId,
                    reasonDescription,
                  )
                }
                isOpen={isCloseCounterModalOpen}
              />
            )}
          </Container>
        </Box>
      )}
    </>
  )
}

export default ProductionView
